import React, { useEffect, useState } from "react";
import Sidebar from "../components/SideBar";
import Loading from "../components/Loading"; // ajuste o caminho conforme sua pasta
import CardIndicator from "../components/dashboardPages/CardIndicator";
import CardIndicatorSecondary from "../components/dashboardPages/CardIndicatorSecondary";
import CardVGV from "../components/dashboardPages/CardVGV";
import { getContagemUnidadesPorCidade, UnidadeCidade } from "../API/dashboardPageService";
import {
  MapPin,
  Building2,
  Building,
  Hammer,
  BriefcaseBusiness,
  Globe,
} from "lucide-react";
import {
  getIndicadores,
  IndicadoresResponse,
  fetchUltimoIPCAAcumulado, 
} from "../API/indicadoresService";

import PrecoMedioChart from "../components/dashboardPages/charts/PrecoMedioChart";
import EvolucaoVendasChart from "../components/dashboardPages/charts/EvolucaoVendasChart";
import GaugeCard from "../components/dashboardPages/charts/GaugeCArd";
import EstoqueCidadeChart from "../components/dashboardPages/charts/EstoqueCidadeChart";
import EstoqueTipologiaChart from "../components/dashboardPages/charts/EstoqueTipologiaChart";
import EstoqueProdutoChart from "../components/dashboardPages/charts/EstoqueProdutoChart";

import "../styles/dashboardPagesStyle/CardIndicatorSecondary.css";
import "../styles/dashboardPagesStyle/CardIndicator.css";
import "../styles/dashboardPagesStyle/CardVSO.css";
import "../styles/dashboardPagesStyle/DashboardPage.css";
import "../styles/dashboardPagesStyle/Charts.css";
import "../styles/dashboardPagesStyle/CardVGV.css";

interface Indicador {
  title: string;
  value: string;
  icon?: React.ReactNode;
  color?: string;
}

interface UnidadesPorCidadeResponse {
  [cidade: string]: UnidadeCidade;
}

export default function DashboardPage() {
  const [indicadores, setIndicadores] = useState<Indicador[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [unidadesCidade, setUnidadesCidade] = useState<UnidadesPorCidadeResponse | null>(null);

  // 👇 adiciona estado para VSO e vendidos/restantes
  const [velocidadeDeVendas, setVelocidadeDeVendas] = useState<number>(0);
  const [numeroVendidos, setNumeroVendidos] = useState<number>(0);
  const [numeroRestantes, setNumeroRestantes] = useState<number>(0);

  useEffect(() => {
    async function fetchData() {
      try {
        const data: IndicadoresResponse = await getIndicadores();

        // 👇 salva o VSO
        setVelocidadeDeVendas(data.vso);

        setIndicadores([
          {
            title: "DÓLAR",
            value: data.dolar ? `R$ ${data.dolar.toFixed(2)}` : "-",
          },
          {
            title: "IPCA",
            value: data.ipcaAcumulado  
              ? `${data.ipcaAcumulado.toFixed(2)}%`
              : "-",
          },
          {
            title: "SELIC",
            value: data.selic  ? `${data.selic .toFixed(2)}%` : "-",
          },
          {
            title: "INCC",
            value: "5,23%", // futuramente puxar de API
          },
          {
            title: "Cidades",
            value: data.totalMunicipios.toString(),
            icon: <MapPin size={"1.4vw"} />,
          },
          {
            title: "Construtoras",
            value: data.totalIncorporadoras.toString(),
            icon: <Hammer size={"1.4vw"} />,
          },
          {
            title: "Empreendimentos",
            value: data.totalEmpreendimentos.toString(),
            icon: <Building2 size={"1.4vw"} />,
          },
          {
            title: "Unidades Mapeadas",
            value: data.totalUnidades.toString(),
            icon: <Building size={"1.4vw"} />,
          },
          {
            title: "VSO",
            value: `${data.vso.toString()}%`, 
            icon: <BriefcaseBusiness size={"1.4vw"} />,
          },
        ]);
      } catch (e: any) {
        console.error("Erro ao buscar indicadores:", e);
        setError("Erro ao carregar indicadores.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function fetchUnidadesCidade() {
      try {
        const response = await getContagemUnidadesPorCidade();
        setUnidadesCidade(response);
      } catch (err) {
        console.error("Erro ao buscar unidades por cidade", err);
      }
    }
    fetchUnidadesCidade();
  }, []);


  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <div style={{ padding: 20, color: "red" }}>{error}</div>;
  }

  return (
    <div style={{ display: "flex" }}>
      <Sidebar />
      <div
        style={{
          padding: 20,
          background: "#f5f5f5",
          minHeight: "100vh",
          flex: 1,
        }}
      >
        <div className="dashboard-conteudo">
          {/* Bloco Esquerdo */}
          <div className="dashboard-esquerda">
            {/* Indicadores de Mercado */}
            <div className="indicadores-mercado">
              <div className="indicadores-container">
                <div className="indicadores-header">
                  <Globe />
                  <span>Indicadores</span>
                  <span>de mercado</span>
                </div>
                <div className="indicadores-grid">
                  {indicadores.slice(0, 4).map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <CardIndicator
                        value={item.value}
                        icon={item.icon}
                        color="#29306A"
                        title=""
                      />
                      <div className="indicador-titulo">{item.title}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Indicadores Secundários */}
            <div className="indicadores-secundarios">
              <div className="indicadores-grid-secondary">
                {indicadores.slice(4).map((item, index) => (
                  <CardIndicatorSecondary
                    key={index + 4}
                    value={item.value}
                    title={item.title}
                    icon={item.icon}
                    color={item.color}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Bloco Direito */}
          <div className="dashboard-direita">
            <GaugeCard
              title="Índice de velocidade de vendas (IVV)"
              value={velocidadeDeVendas}
              vendidos={numeroVendidos}
              restantes={numeroRestantes}
            />
          </div>
        </div>

        <div className="graficos-principais">
          <div className="grafico">
            <PrecoMedioChart unidadesCidade={unidadesCidade}/>
          </div>
          <div className="grafico">
            <EvolucaoVendasChart />
          </div>
        </div>
        <div className="graficos-principais">
          <div className="grafico-secundarios">
            <CardVGV />
          </div>
          <div className="grafico-secundarios">
            <EstoqueProdutoChart />
          </div>
          <div className="grafico-secundarios">
            <EstoqueTipologiaChart />
          </div>
        </div>
        <div>
          <EstoqueCidadeChart unidadesCidade={unidadesCidade}/>
        </div>
      </div>
    </div>
  );
}
